import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Filter } from "lucide-react";
import { PeriodicTable } from "../components/PeriodicTable";
import { ElementDetailDialog } from "../components/ElementDetailDialog";
import { CATEGORY_LABELS, CATEGORY_BG, HAZARD_LABELS, HAZARD_BG } from "../lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HAZARDS = ["extreme", "high", "warning", "safe", "unknown"];

export default function Home() {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState(null);
  const [hazFilter, setHazFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/elements`).then((r) => {
      setElements(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Auto-open element from ?el=Pb query param (cross-link from disease pages)
  useEffect(() => {
    if (!elements.length) return;
    const target = searchParams.get("el");
    if (!target) return;
    const match = elements.find(
      (e) => e.s?.toLowerCase() === target.toLowerCase()
    );
    if (match) {
      setSelected(match);
      setOpen(true);
      searchParams.delete("el");
      setSearchParams(searchParams, { replace: true });
    }
  }, [elements, searchParams, setSearchParams]);

  const categories = useMemo(() => {
    const set = {};
    elements.forEach((e) => { set[e.cat] = (set[e.cat] || 0) + 1; });
    return Object.keys(set).sort();
  }, [elements]);

  const matches = (el) => {
    if (catFilter && el.cat !== catFilter) return false;
    if (hazFilter && el.hazard !== hazFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!el.name.toLowerCase().includes(q) && !el.s.toLowerCase().includes(q) && String(el.n) !== q) {
        return false;
      }
    }
    return true;
  };

  const handleSelect = (el) => {
    setSelected(el);
    setOpen(true);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="home-page">
      {/* HERO */}
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
                <span className="w-8 h-px bg-zinc-950" />
                Reference · Laboratory Health & Safety
              </div>
              <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
                Every Element.<br />
                <span className="bg-zinc-950 text-white px-2 inline-block">Every Hazard.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-700 max-w-xl">
                Click any of the <strong>118 elements</strong> to view symptoms, complications, precautions and first-aid treatment.
                Pair with <strong>30 industrial-grade safety posters</strong> for full lab readiness.
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="grid grid-cols-2 gap-px bg-zinc-950 border-2 border-zinc-950">
                <Stat n="118" l="Elements" />
                <Stat n="30" l="Posters" />
                <Stat n="10" l="Categories" />
                <Stat n="5" l="Hazard Levels" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="border-b-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4">
              <div className="flex items-center gap-2 border-2 border-zinc-950 bg-white px-3 py-2">
                <Search size={16} strokeWidth={2.5} />
                <input
                  data-testid="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by symbol, name or number…"
                  className="w-full bg-transparent border-0 outline-none font-mono text-sm placeholder:text-zinc-500"
                />
              </div>
            </div>
            <div className="md:col-span-8 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 flex items-center gap-1.5">
                <Filter size={12} strokeWidth={2.5} /> Hazard:
              </span>
              {HAZARDS.map((h) => (
                <button
                  key={h}
                  data-testid={`filter-hazard-${h}`}
                  onClick={() => setHazFilter(hazFilter === h ? null : h)}
                  className={`${HAZARD_BG[h]} border-2 border-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
                    hazFilter === h ? "neo-shadow -translate-y-0.5" : ""
                  } transition-all`}
                >
                  {HAZARD_LABELS[h]}
                </button>
              ))}
              {(catFilter || hazFilter || query) && (
                <button
                  data-testid="clear-filters"
                  onClick={() => { setCatFilter(null); setHazFilter(null); setQuery(""); }}
                  className="font-mono text-[10px] uppercase tracking-widest underline ml-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PERIODIC TABLE */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="font-mono text-sm text-zinc-600 py-12 text-center" data-testid="elements-loading">
              Loading elements…
            </div>
          ) : (
            <PeriodicTable elements={elements} onSelect={handleSelect} highlight={matches} />
          )}
        </div>
      </section>

      {/* LEGEND */}
      <section className="border-t-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3">Category Legend</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setCatFilter(catFilter === c ? null : c)}
                className={`${CATEGORY_BG[c]} border-2 border-zinc-950 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
                  catFilter === c ? "neo-shadow -translate-y-0.5" : ""
                } transition-all`}
              >
                {CATEGORY_LABELS[c]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <ElementDetailDialog element={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div className="bg-white px-4 py-3">
    <div className="font-heading font-black text-3xl leading-none">{n}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1">{l}</div>
  </div>
);
